import React from "react"

import {
  Alert,
  AlertTitle,
  Badge,
  MultiSelect,
  type MultiSelectOption,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  useEligibleParentMetrics,
  type AggregationType,
  type DerivedMetricType,
} from "../../hooks/useEligibleParentMetrics"

interface EligibleParentsPickerProps {
  metrics: MetricRecordData[]
  targetMetricType: DerivedMetricType
  aggregationType?: AggregationType
  selectedParentIds: string[]
  selfId?: string
  onChange: (parentIds: string[]) => void
}

/**
 * Picker for selecting parent metrics when creating a derived aggregate metric.
 * Renders eligible/ineligible counts as badges, surfaces unresolved selected
 * parent IDs in a danger Alert, and delegates the listbox to MultiSelect with
 * disabled options for ineligible parents.
 */
export const EligibleParentsPicker: React.FC<EligibleParentsPickerProps> = ({
  metrics,
  targetMetricType,
  aggregationType,
  selectedParentIds,
  selfId,
  onChange,
}) => {
  const {
    entries,
    eligibleCount,
    ineligibleCount,
    unresolvedSelectedParentIds,
  } = useEligibleParentMetrics({
    metrics,
    targetMetricType,
    aggregationType,
    alreadySelectedParentIds: selectedParentIds,
    selfId,
  })

  const options: MultiSelectOption[] = entries
    .filter(({ metric }) => Boolean(metric.id))
    .map(({ metric, eligibility }) => {
      const unitSuffix = [metric.output_type, metric.metric_metadata?.units]
        .filter(Boolean)
        .join(" · ")
      const baseLabel =
        metric.metric_display_name || metric.metric_name || "(unnamed metric)"
      return {
        value: metric.id as string,
        label: unitSuffix ? `${baseLabel} (${unitSuffix})` : baseLabel,
        disabled: !eligibility.eligible,
      }
    })

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex items-center gap-2 text-xs text-coval-text-muted"
        aria-live="polite"
      >
        <Badge
          variant="default"
          aria-label={`${eligibleCount} metrics available`}
        >
          {eligibleCount} available
        </Badge>
        {ineligibleCount > 0 && (
          <Badge
            variant="outline"
            aria-label={`${ineligibleCount} metrics unavailable`}
          >
            {ineligibleCount} unavailable
          </Badge>
        )}
      </div>

      {unresolvedSelectedParentIds.length > 0 && (
        <Alert
          variant="danger"
          aria-label="Selected parents could not be loaded"
        >
          <AlertTitle>
            {unresolvedSelectedParentIds.length === 1
              ? "1 selected parent could not be loaded:"
              : `${unresolvedSelectedParentIds.length} selected parents could not be loaded:`}
          </AlertTitle>
          <ul className="mt-1 list-disc pl-4 font-mono opacity-90">
            {unresolvedSelectedParentIds.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
          <p className="mt-1 opacity-90">
            Remove them before adding more parents — unit enforcement is paused
            until they are resolved.
          </p>
        </Alert>
      )}

      {options.length === 0 ? (
        <p className="text-sm text-coval-text-muted px-3 py-2">
          No metrics available in this organization.
        </p>
      ) : (
        <MultiSelect
          aria-label="Parent metric options"
          options={options}
          value={selectedParentIds}
          onValueChange={onChange}
          searchable
          placeholder="Select parent metrics…"
        />
      )}
    </div>
  )
}
